import React, { RefObject, useState } from 'react';
import { useCombobox } from 'downshift';

import {
    InjectedIntl,
    injectIntl,
} from 'react-intl';

import {
    FetchOrganizationsComponent,
    Organization,
    DatabaseId,
    Country,
    Locale,
    setRef,
    assertValidFunction,
    handleStateChange,
    ErrorId,
    ExtendedErrorId,
} from '@sheerid/jslib';

interface TypeaheadProps {
  onChange: any;
  programId: DatabaseId;
  inputHtmlId: string;
  autoFocus?: boolean;
  className?: string;
  countryCode?: Country;
  disabled?: boolean;
  minimumSearchValueLength?: number;
  placeholder?: string;
  value?: Organization;
  openOrgSearchEnabled?: boolean;
  orgSearchUrl?: string;
  orgSearchTags?: string;
  isRequired?: boolean;
  locale?: Locale;
  urlAddSchoolForm?: string;
  errorId?: ErrorId | ExtendedErrorId;
  isErrored?: boolean;
  intl?: InjectedIntl; // eslint-disable-line
}

const Typeahead = ({
    onChange,
    programId,
    className,
    countryCode,
    disabled,
    isErrored,
    errorId,
    minimumSearchValueLength,
    placeholder,
    value,
    inputHtmlId,
    openOrgSearchEnabled,
    orgSearchUrl,
    orgSearchTags,
    isRequired,
    locale,
    urlAddSchoolForm,
    intl,
}: TypeaheadProps) => {
    const [inputValue, setInputValue] = useState('');
    const [loadedOrgs, setLoadedOrgs] = useState([]);

    assertValidFunction(onChange);

    const itemToString = (item: Organization) => (item ? item.name : '');

    const {
        isOpen, highlightedIndex, getMenuProps, getItemProps, getInputProps, getComboboxProps,
    } = useCombobox({
        itemToString,
        id: inputHtmlId,
        items: loadedOrgs,
        onStateChange: (changes) => handleStateChange(changes, onChange, openOrgSearchEnabled),
        onInputValueChange: ({ inputValue }) => {
            setInputValue(inputValue);
        },
        selectedItem: value,
        onSelectedItemChange: ({ selectedItem }) => {
            onChange(selectedItem);
        },
    });

    // Maintain same labelledby value as previous versions even though label has "for" attribute
    const comboboxArgs = { 'aria-labelledby': `${inputHtmlId}-label` };

    const inputRef: RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    const errorText = (errorIdForFormat: string) => intl?.formatMessage({ id: `errorId.${errorIdForFormat}`, defaultMessage: 'Invalid Value' });

    return (
        <>
            <div className="sid-organization-list" {...getComboboxProps(comboboxArgs)}>
                <input
                    {...getInputProps({ ref: inputRef })}
                    className={`sid-l-full-width sid-hidden-placeholder sid-text-input ${className} ${isErrored ? 'sid-text-input--error' : ''}`}
                    disabled={disabled}
                    id={inputHtmlId}
                    placeholder={placeholder}
                    aria-required={isRequired}
                    aria-describedby={`${isErrored ? 'sid-organization-name-error' : ''}`}
                    aria-invalid={isErrored}
                    aria-errormessage={isErrored ? errorText(errorId) : ''}
                    ref={(input) => setRef('organization', input)}
                />

                {/* The menu (div) is always mounted. Its child is conditional. Fixes "Downshift error: getMenuProps() was not applied correctly" */}
                <div {...getMenuProps()}>
                    {isOpen ? (
                        <FetchOrganizationsComponent
                            countryCode={countryCode}
                            getItemProps={getItemProps}
                            highlightedIndex={highlightedIndex}
                            itemToString={itemToString}
                            minimumSearchValueLength={minimumSearchValueLength}
                            onLoaded={(loadedOrganizations: React.SetStateAction<any[]>) => setLoadedOrgs(loadedOrganizations)}
                            programId={programId}
                            searchValue={inputValue}
                            orgSearchUrl={orgSearchUrl}
                            orgSearchTags={orgSearchTags}
                            locale={locale}
                            urlAddSchoolForm={urlAddSchoolForm}
                            openOrgSearchEnabled={openOrgSearchEnabled}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export const TypeaheadComponent = injectIntl(Typeahead);
